import { IS_BROWSER } from '$fresh/runtime.ts'
import { useEffect } from 'preact/hooks'
import { useCart } from 'apps/vtex/hooks/useCart.ts'

/**
 * UtmSegmentSync
 *
 * Fixes the campaign discount not applying when a marketing email lands the user on a
 * CDN-cached page (e.g. the homepage). Cached HTML is served without running the deco origin,
 * so the `vtex_segment` cookie (which carries `utm_campaign`) is never set and the VTEX
 * OrderForm `marketingData` stays empty — no UTM promotion.
 *
 * This island runs client-side on every page (mounted from routes/_app.tsx), so it executes
 * even when the surrounding HTML came from cache. When the current URL carries `utm_*` params it:
 *   1. registers them into the VTEX session -> the server sets `vtex_segment` (same result an
 *      un-cached page like /novidades gets), so catalog prices and the cart honor the campaign;
 *   2. stamps them onto the current OrderForm `marketingData` (best-effort; no-ops if no cart yet),
 *      preserving any existing coupon.
 *
 * `vtex_segment` is HttpOnly, so step 1 goes through the VTEX Sessions API (the server sets the
 * cookie) rather than writing it from JS.
 */

const UTM_KEYS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utmi_campaign',
    'utmi_page',
    'utmi_part',
] as const

export default function UtmSegmentSync() {
    const { cart, sendAttachment } = useCart()

    useEffect(() => {
        if (!IS_BROWSER) return

        const params = new URLSearchParams(globalThis.location.search)
        const utm: Record<string, string> = {}
        for (const key of UTM_KEYS) {
            const value = params.get(key)
            if (value) utm[key] = value
        }

        // Nothing to do without a campaign/source to attribute.
        if (!utm.utm_campaign && !utm.utm_source) return

        // Run once per session per campaign to avoid redundant calls on SPA navigation.
        const marker = `utm_synced:${utm.utm_campaign ?? utm.utm_source}`
        try {
            if (globalThis.sessionStorage.getItem(marker)) return
        } catch {
            // sessionStorage may be unavailable (privacy mode); proceed without the guard.
        }

        ;(async () => {
            try {
                // 1) Register the campaign into the VTEX session -> server sets `vtex_segment`.
                await fetch('/api/sessions', {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'content-type': 'application/json' },
                    body: JSON.stringify({
                        public: Object.fromEntries(
                            Object.entries(utm).map(([key, value]) => [key, { value }]),
                        ),
                    }),
                })

                // 2) Stamp `marketingData` onto the current OrderForm (keeps any existing coupon).
                //    Only when an OrderForm already exists: the `updateAttachment` action reads the
                //    orderFormId from the cart cookie and throws "Order form ID is required" when it
                //    is missing, so we must NOT call it for cartless visitors. This is the common
                //    case for marketing-email traffic (first touch, no cart yet). The segment set in
                //    step 1 still ensures VTEX applies the campaign once the cart is created.
                if (cart.value?.orderFormId) {
                    await sendAttachment({
                        attachment: 'marketingData',
                        body: {
                            utmSource: utm.utm_source,
                            utmMedium: utm.utm_medium,
                            utmCampaign: utm.utm_campaign,
                            utmiCampaign: utm.utmi_campaign,
                            utmiPage: utm.utmi_page,
                            utmiPart: utm.utmi_part,
                            coupon: cart.value?.marketingData?.coupon,
                        },
                    })
                }

                try {
                    globalThis.sessionStorage.setItem(marker, '1')
                } catch {
                    // ignore storage failures
                }
            } catch (error) {
                console.error('[UtmSegmentSync] failed to sync UTM to VTEX', error)
            }
        })()
    }, [])

    return null
}
